import {Injectable} from '@angular/core';
import {Actions} from '@ngrx/effects';
import {BehaviorSubject, combineLatestWith, Observable, of} from 'rxjs';
import {map, take, tap} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {ManagementStoreService} from './management-store.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {OperatorLocksEffects} from '@emm/management/services/effects/operator-locks-effects.service';
import {Node} from '@emm/core/models/node.model';
import {OperatorLockItem} from '@emm/core/models/operator-lock-item.model';
import {LocationStatus} from '@emm/core/models/location-status.model';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {NotificationService} from '@emm/shared/services/notification.service';


@Injectable({ providedIn: 'root' })
export class OperatorLocksFacadeService {

    emergencyItemDetail$ = new BehaviorSubject<any>(null);

    centerLoading$ = of([]).pipe(
        combineLatestWith([
            this.managementStore.isLoading$.base,
            this.managementStore.isLoading$.emergencyItems
        ]),
        map((sources: any[]) => R.any(R.equals(true), sources))
    );

    statuses$ = new BehaviorSubject<any>(null);
    items$ = new BehaviorSubject<any>(null);

    constructor(
        private actions$: Actions,
        private emergencyItemsEffects: OperatorLocksEffects,
        private managementStore: ManagementStoreService,
        private notificationService: NotificationService,
        public api: ManagementApiService,
    ) {}

    init(): any {
        this.api.getAllStatuses().subscribe(
            {
                next: (result) => {
                    const masterMenuStatuses = RA.isNonEmptyArray(result.filter(status =>
                        status?.category?.includes('Master Menu'))) ? result.filter(status =>
                        status?.category?.includes('Master Menu')).map(this.mapOperatorLockToNode) : [];
                    const emergencyStatuses = RA.isNonEmptyArray(result) ? result.map(this.mapOperatorLockToNode) : [];
                    const nodes = [
                        {
                            hasChildren: true,
                            id: 'MMS_SELECTIONS',
                            name: 'Master Menu Selections',
                            children: [
                                {
                                    hasChildren: true,
                                    id: 'MMS_RESTRICTIONS',
                                    name: 'MMS Restrictions',
                                    children: masterMenuStatuses.filter(status =>
                                        status?.data?.subCategory === 'MMS Restrictions')
                                },
                                {
                                    hasChildren: true,
                                    id: 'MMS_OPTIONS',
                                    name: 'MMS Options',
                                    children: masterMenuStatuses.filter(status =>
                                        status?.data?.subCategory === 'MMS Options')
                                }
                            ]
                        },
                        {
                            hasChildren: true,
                            id: 'OPTIONAL_MENU_SELECTION',
                            name: 'Optional Menu Selection',
                            children: emergencyStatuses.filter(status => status?.data?.category === 'Optional Menu Selection')
                        },
                        {
                            hasChildren: true,
                            id: 'EQUIPMENT_FAILURE',
                            name: 'Equipment Failure',
                            children: emergencyStatuses.filter(status => status?.data?.category === 'Equipment Failure')
                        },
                        {
                            hasChildren: true,
                            id: 'INGREDIENT_SHORTAGE',
                            name: 'Ingredient Shortage',
                            children: emergencyStatuses.filter(status => status?.data?.category === 'Ingredient Shortage')
                        },
                    ];
                    this.statuses$.next(nodes);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            }
        );
    }

    startProgressBar() {
        this.managementStore.dispatch.startLoading();
    }

    stopProgressBar() {
        this.managementStore.dispatch.stopLoading();
    }

    select(item: { tag: string }): void {
        this.emergencyItemDetail$.next(item);
    }

    getItemsByEmergency(tag: string): Observable<Node<OperatorLockItem>[]> {
        const success$ = this.api.getItemsByEmergency(tag).pipe(
            tap(items => this.items$.next(items)),
            map((emergencyItems: OperatorLockItem[]) =>
                RA.isNonEmptyArray(emergencyItems) ? emergencyItems.map(this.mapItemToNode) : []
            ));
        return success$.pipe(take(1));
    }

    public mapItemToNode(item: OperatorLockItem): Node<OperatorLockItem> {
        return {
            data: item,
            hasChildren: item.grouping,
            id: item.tag,
            name: item.name,
        };
    }

    public mapOperatorLockToNode(locationStatus: LocationStatus): Node<LocationStatus> {
        return {
            data: locationStatus,
            hasChildren: locationStatus.items !== null,
            id: locationStatus.tag,
            name: locationStatus.name,
            children: locationStatus.items
        };
    }

    openAddDialog(): void {
        this.managementStore.dispatch.openAddEmergencyItemDialog();
    }

    uploadChildItems(emergencyTag: string, file: FormData): any {
        this.api.uploadOperatorLockItems(emergencyTag, file).subscribe({
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    downloadSampleFile(): Observable<any> {
        return this.api.downloadSampleFile();
    }

    removeChildItem(emergencyTag: string, itemTag: string): any {
        this.api.deleteEmergencyItem(emergencyTag, itemTag).subscribe();
    }

    updateStatus(emergencyTag: string, statusMessage: string, active: boolean, subType?: string): any {
        this.api.updateStatus(emergencyTag, statusMessage, active, subType ? subType : null).subscribe();
    }
}
